import { SlidersHorizontalIcon } from "lucide-react";

import { PlayyardSkyPreviewPicker } from "./PlayyardSkyPreviewPicker";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";

import {
  petYardWeatherConditionToLabel,
  playyardSkyPhaseToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import {
  PetYardWeatherResponse,
  PlayyardSky,
  PlayyardSkyPreview,
} from "@/types";

const YARD_TIMEZONE = "America/Chicago";

export const PlayyardSkyStatus = ({
  sky,
  weather,
  now,
  preview,
  isAdmin,
  setPreview,
}: {
  sky: PlayyardSky;
  weather: PetYardWeatherResponse;
  now: number;
  preview: PlayyardSkyPreview;
  isAdmin: boolean;
  setPreview: (preview: PlayyardSkyPreview) => void;
}) => {
  /* Helpers */
  const isPreviewing =
    preview.phase !== null ||
    preview.condition !== null ||
    preview.isWindy ||
    preview.isFreezing;
  const timeLabel = isPreviewing
    ? playyardSkyPhaseToLabel[sky.phase]
    : dates.formatTime(new Date(now), weather?.timezone ?? YARD_TIMEZONE);
  const parts = [timeLabel, petYardWeatherConditionToLabel[sky.condition]];

  /* Render */
  return (
    <Row className="absolute left-3 top-3 z-[60] items-center gap-2">
      <Row
        className={cn(
          "items-center gap-2 rounded-full bg-black/25 px-3 py-1 backdrop-blur-sm",
          isPreviewing && "bg-violet-900/60",
        )}
      >
        {isPreviewing ? (
          <Text className="font-mono text-[9px] font-black uppercase tracking-[0.12em] text-violet-200">
            Preview
          </Text>
        ) : null}
        <Text className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-white [text-shadow:0_1px_2px_rgb(0_0_0_/_0.35)]">
          {parts.join(" · ")}
        </Text>
      </Row>
      {isAdmin ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-7 w-7 rounded-full bg-black/25 p-0 text-white backdrop-blur-sm hover:bg-black/40 hover:text-white"
            >
              <SlidersHorizontalIcon className="h-3.5 w-3.5" />
              <Text className="sr-only">Preview sky</Text>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-80 rounded-none border-2 border-slate-900 bg-[#fffaf0] p-3 shadow-[4px_4px_0_#172033]"
          >
            <PlayyardSkyPreviewPicker
              preview={preview}
              setPreview={setPreview}
            />
          </PopoverContent>
        </Popover>
      ) : null}
    </Row>
  );
};
